import { AlertTriangle, CheckCircle2, Loader2, X, XCircle, ZoomIn } from "lucide-react";
import clsx from "clsx";
import { MAP_COLORS } from "@/components/MapPreview";
import { Button } from "@/components/ui";
import { useLanguage } from "@/i18n";
import { formatNumber } from "@/lib/format";
import type { FeatureQa } from "@/lib/api";
import type { GeoJsonGeometry } from "@/lib/types";

/**
 * The overlap & sliver check's result, floating over the top of the map: a
 * solid card (the map must not show through the text) with the summary on top
 * and, on request, a scrolling list of the problems with a Zoom per row.
 */
export default function QaResultsCard({
  qa,
  loading,
  error,
  listOpen,
  onToggleList,
  onClose,
  onZoom,
}: {
  qa: FeatureQa | null;
  loading: boolean;
  error: string | null;
  listOpen: boolean;
  onToggleList: () => void;
  onClose: () => void;
  /** featureId is set for a sliver (one property), so it can be selected too. */
  onZoom: (geometry: GeoJsonGeometry, featureId?: number) => void;
}) {
  const { t, lang } = useLanguage();
  const p = t.projectWorkspacePage;
  const issues = qa ? qa.overlap_count + qa.sliver_count : 0;
  const area = (m2: number) => `${formatNumber(Math.round(m2 * 100) / 100, lang)} m²`;

  const state = error ? "error" : loading || !qa ? "loading" : issues > 0 ? "found" : "clear";
  const icon = {
    error: <XCircle className="h-4 w-4 text-status-critical" />,
    loading: <Loader2 className="h-4 w-4 animate-spin text-accent" />,
    found: <AlertTriangle className="h-4 w-4 text-status-warning" />,
    clear: <CheckCircle2 className="h-4 w-4 text-status-good" />,
  }[state];
  const rule = {
    error: "bg-status-critical",
    loading: "bg-accent",
    found: "bg-status-warning",
    clear: "bg-status-good",
  }[state];

  return (
    <div className="relative overflow-hidden rounded-md border border-line bg-surface text-ink shadow-xl">
      <span className={clsx("absolute inset-x-0 top-0 h-[3px]", rule)} aria-hidden="true" />

      <div className="flex items-center gap-3 px-3 py-2.5">
        <span className="flex-none">{icon}</span>
        <div className="min-w-0 flex-1 text-xs leading-snug">
          {state === "error" && error}
          {state === "loading" && p.qaChecking}
          {state === "clear" && qa && p.qaNone(qa.checked)}
          {state === "found" && qa && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Count color={MAP_COLORS.qaOverlap} value={formatNumber(qa.overlap_count, lang)} label={p.qaOverlaps} />
              <Count color={MAP_COLORS.qaSliver} value={formatNumber(qa.sliver_count, lang)} label={p.qaSlivers} />
              <span className="text-ink-muted">{p.qaCheckedOf(formatNumber(qa.checked, lang))}</span>
            </div>
          )}
        </div>
        {state === "found" && (
          <Button size="sm" variant="secondary" onClick={onToggleList}>
            {listOpen ? p.qaHideList : p.qaShowList}
          </Button>
        )}
        <button
          type="button"
          onClick={onClose}
          title={p.qaClose}
          aria-label={p.qaClose}
          className="flex h-7 w-7 flex-none items-center justify-center rounded text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {state === "found" && qa && listOpen && (
        <div className="border-t border-line">
          <ul className="thin-scroll max-h-64 divide-y divide-line overflow-auto">
            {qa.overlaps.map((o) => (
              <li key={`o${o.a_id}-${o.b_id}`} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                <Kind color={MAP_COLORS.qaOverlap} label={p.qaOverlap} />
                <span className="min-w-0 flex-1 truncate font-medium" title={`${o.a_label} ↔ ${o.b_label}`}>
                  {o.a_label} <span className="text-ink-muted">↔</span> {o.b_label}
                </span>
                <span className="flex-none text-right tabular-nums">
                  {area(o.area_m2)}
                  <span className="ml-1.5 text-ink-muted">{p.qaPercentOfSmaller(o.percent_of_smaller)}</span>
                </span>
                <ZoomButton
                  label={p.qaZoom}
                  disabled={!o.geometry}
                  onClick={() => o.geometry && onZoom(o.geometry)}
                />
              </li>
            ))}
            {qa.slivers.map((sl) => (
              <li key={`s${sl.id}`} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                <Kind color={MAP_COLORS.qaSliver} label={p.qaSliver} />
                <span className="min-w-0 flex-1 truncate font-medium" title={sl.label}>
                  {sl.label}
                </span>
                <span className="flex-none text-right tabular-nums">
                  {area(sl.area_m2)}
                  <span className="ml-1.5 text-ink-muted">{sl.reason === "thin" ? p.qaVeryThin : p.qaVerySmall}</span>
                </span>
                <ZoomButton label={p.qaZoom} onClick={() => onZoom(sl.geometry, sl.id)} />
              </li>
            ))}
          </ul>
          {(qa.overlaps.length < qa.overlap_count || qa.slivers.length < qa.sliver_count) && (
            <p className="border-t border-line bg-surface-sunken px-3 py-1.5 text-2xs text-ink-muted">
              {p.qaListCapped(qa.overlaps.length + qa.slivers.length)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Count({ color, value, label }: { color: string; value: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden="true" />
      <span className="font-display text-base font-bold leading-none tabular-nums">{value}</span>
      <span className="text-ink-muted">{label}</span>
    </span>
  );
}

function Kind({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="w-[4.5rem] flex-none rounded-sm px-1.5 py-0.5 text-center text-2xs font-semibold uppercase tracking-wide"
      style={{ color, backgroundColor: `${color}14` }}
    >
      {label}
    </span>
  );
}

function ZoomButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex flex-none items-center gap-1 rounded px-1.5 py-0.5 font-medium text-accent-solid transition hover:bg-accent-subtle disabled:invisible"
    >
      <ZoomIn className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
